import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { Subject, catchError, firstValueFrom, map, of, pairwise, takeUntil } from 'rxjs'
import { ToastService } from '@common/services'
import {
	ElectronicProcessBook,
	ElectronicProcessBookErrorUnit,
	ElectronicProcessBookStatus,
	bookFailureReason
} from '@models/planning/electronic-process-book.model'
import { ElectronicProcessBookService } from '@services/electronic-process-book.service'
import { ElectronicProcessBookSocketService } from '@services/socket/electronic-process-book-socket.service'
import { RealtimeFallbackPoller } from '@services/realtime/realtime-fallback-poller'
import { AccountService } from '@services/account.service'
import { AuthService } from '@services/auth.service'
import { PlatformService } from '@services/platform.service'
import { NativeDocumentService } from '@services/native-document.service'
import { downloadByLink } from '@utils/download-link.util'
import { ElectronicProcessBookViewerComponent } from './electronic-process-book-viewer.component'

/**
 * Livro eletrônico do processo (spec 001): pedir a geração, acompanhar em tempo real (fila, verificação, carimbo,
 * montagem), o histórico das gerações e, com os arquivos no prazo de 72 horas, visualizar e baixar. A geração roda no
 * workload: o usuário pode sair da tela e recebe o aviso quando terminar.
 */
@Component({
	selector: 'app-electronic-process-books',
	templateUrl: './electronic-process-books.component.html',
	styleUrl: './electronic-process-books.component.scss',
	standalone: false
})
export class ElectronicProcessBooksComponent implements OnInit, OnChanges, OnDestroy {
	@Input() processId: string | null = null
	@Input() processNumber: string | null = null

	books: ElectronicProcessBook[] = []
	totalElements = 0
	page = 0
	loading = false
	requesting = false
	downloadingId: string | null = null
	expanded = new Set<string>()
	authors: Record<string, string | null> = {}

	readonly pageSize = 10
	readonly Status = ElectronicProcessBookStatus

	private poller?: RealtimeFallbackPoller
	private destroy$ = new Subject<void>()
	private subscribedProcessId: string | null = null

	constructor(
		private modal: NgbModal,
		private toast: ToastService,
		private bookService: ElectronicProcessBookService,
		private bookSocket: ElectronicProcessBookSocketService,
		private accountService: AccountService,
		private authService: AuthService,
		private platform: PlatformService,
		private nativeDocument: NativeDocumentService
	) {}

	ngOnInit(): void {
		this.bookSocket.events$.pipe(takeUntil(this.destroy$)).subscribe(book => this.onBookEvent(book))

		// De volta depois de uma queda: busca o que passou fora do ar.
		this.bookSocket.status$.pipe(pairwise(), takeUntil(this.destroy$)).subscribe(([previous, current]) => {
			if (previous !== 'connected' && current === 'connected') this.reload()
		})

		// Sem conexão e com geração em andamento, a tela segue por consulta a cada 5 s (RF 7).
		this.poller = new RealtimeFallbackPoller({
			intervalMs: 5_000,
			isConnected: () => this.bookSocket.connected || !this.active,
			resync: () => void this.refreshActive().finally(() => this.poller?.settle())
		})
		this.poller.start()
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['processId']) {
			this.watch()
			this.reload()
		}
	}

	ngOnDestroy(): void {
		this.poller?.stop()
		if (this.subscribedProcessId) this.bookSocket.unsubscribeProcess(this.subscribedProcessId)
		void this.bookSocket.disconnect()
		this.destroy$.next()
		this.destroy$.complete()
	}

	/** A geração na fila ou em andamento (no máximo uma por processo). */
	get active(): ElectronicProcessBook | null {
		return this.books.find(book => book.active) ?? null
	}

	/** O livro mais recente com arquivos no prazo, para abrir sem procurar no histórico. */
	get latestAvailable(): ElectronicProcessBook | null {
		return this.books.find(book => book.available) ?? null
	}

	/** Pede o livro (RF 1 a 4). Recusado (sem documento, paginação em andamento ou desatualizada), o motivo vem do servidor. */
	async request(): Promise<void> {
		if (!this.processId || this.requesting || this.active) return

		this.requesting = true

		try {
			const result = await firstValueFrom(this.bookService.request(this.processId))

			this.upsert(result.book)
			this.toast.info(
				result.created
					? 'Geração do livro iniciada. Você pode sair desta tela; avisaremos quando terminar.'
					: 'Já havia uma geração do livro em andamento: você passou a acompanhá-la.'
			)
		} catch (error) {
			if (error instanceof HttpErrorResponse && (error.status === 503 || error.status === 0)) {
				this.toast.error('O gerador do livro está indisponível no momento. Tente novamente em instantes.')
			}
		} finally {
			this.requesting = false
		}
	}

	open(book: ElectronicProcessBook): void {
		if (!this.processId || !book.available) return

		const modalRef = this.modal.open(ElectronicProcessBookViewerComponent, { fullscreen: true, backdrop: 'static', keyboard: true })
		const viewer = modalRef.componentInstance as ElectronicProcessBookViewerComponent
		viewer.processId = this.processId
		viewer.book = book
		viewer.processNumber = this.processNumber
	}

	/** Download pelo gerenciador de downloads do navegador, sem carregar o PDF na página (RF 21). */
	async download(book: ElectronicProcessBook): Promise<void> {
		if (!this.processId || !book.available || this.downloadingId) return

		this.downloadingId = book.id

		try {
			const link = await firstValueFrom(this.bookService.downloadUrl(this.processId, book.id))

			if (this.platform.mobile) {
				await this.nativeDocument.openPdf(link.url, link.filename ?? 'livro-eletronico.pdf')
			} else {
				this.saveLink(link.url, link.filename)
			}
		} catch (error) {
			// Venceu entre a lista e o clique: a tela passa a mostrar "Expirado".
			if (error instanceof HttpErrorResponse && error.status === 410) this.reload()
		} finally {
			this.downloadingId = null
		}
	}

	/** Entrega o link ao gerenciador de downloads do navegador. Separado para o teste não navegar a página. */
	protected saveLink(url: string, filename?: string | null): void {
		downloadByLink(url, filename)
	}

	toggle(book: ElectronicProcessBook): void {
		if (this.expanded.has(book.id)) this.expanded.delete(book.id)
		else this.expanded.add(book.id)
	}

	reason(unit: ElectronicProcessBookErrorUnit): string {
		return bookFailureReason(unit)
	}

	/** A falha aponta paginação divergente: a saída é reprocessar a paginação (RF 25). */
	paginationIssue(book: ElectronicProcessBook): boolean {
		return book.failures.some(unit => unit.code === 'PAGE_COUNT_MISMATCH' || unit.code === 'PAGINATION_NOT_CONTIGUOUS')
	}

	/** A geração anterior que esta "Sem alterações" reaproveitou (RF 11), quando está na página. */
	reusedFrom(book: ElectronicProcessBook): ElectronicProcessBook | null {
		return book.reusedFromId ? (this.books.find(it => it.id === book.reusedFromId) ?? null) : null
	}

	onPageChange(page: number): void {
		this.page = page
		this.load()
	}

	reload(): void {
		this.page = 0
		this.load()
	}

	private watch(): void {
		if (this.subscribedProcessId) this.bookSocket.unsubscribeProcess(this.subscribedProcessId)

		this.subscribedProcessId = this.processId
		if (!this.processId) return

		this.bookSocket.connect(this.accountService.tenantId)
		this.bookSocket.subscribeProcess(this.processId)
	}

	private load(): void {
		if (!this.processId) {
			this.books = []
			return
		}

		this.loading = true

		this.bookService.history(this.processId, this.page, this.pageSize).subscribe({
			next: page => {
				this.books = page?.content ?? []
				this.totalElements = page?.totalElements ?? this.books.length
				this.loading = false

				// Uma falha recente já aparece aberta: é o que o usuário veio ver.
				if (this.page === 0 && this.books[0]?.failed) this.expanded.add(this.books[0].id)

				this.resolveAuthors(this.books)
			},
			error: () => (this.loading = false)
		})
	}

	private async refreshActive(): Promise<void> {
		const active = this.active
		if (!this.processId || !active?.id) return

		try {
			this.onBookEvent(await firstValueFrom(this.bookService.detail(this.processId, active.id)))
		} catch {
			/* o próximo ciclo tenta de novo */
		}
	}

	private onBookEvent(book: ElectronicProcessBook): void {
		if (!book?.id || book.processId !== this.processId) return

		const previous = this.books.find(it => it.id === book.id)
		this.upsert(book)

		// Terminou agora: o histórico pode ter mudado (a geração reaproveitada teve o prazo renovado).
		if (previous?.active && !book.active) this.reload()
	}

	/** Atualiza a geração na lista, ou a põe no topo quando é nova. */
	private upsert(book: ElectronicProcessBook): void {
		const index = this.books.findIndex(it => it.id === book.id)

		if (index >= 0) {
			this.books = this.books.map((it, i) => (i === index ? book : it))
		} else if (this.page === 0) {
			this.books = [book, ...this.books].slice(0, this.pageSize)
			this.totalElements++
		}

		this.resolveAuthors([book])
	}

	/** Nome de quem pediu, quando o usuário pode consultar; sem permissão, a entrada fica sem autor. */
	private resolveAuthors(books: ElectronicProcessBook[]): void {
		const tenantId = this.accountService.tenantId

		new Set(books.map(it => it.createdBy).filter(id => id && !(id in this.authors))).forEach(id => {
			this.authors[id] = null

			this.authService
				.detailUser(id, tenantId)
				.pipe(
					map(user => user?.name ?? null),
					catchError(() => of(null))
				)
				.subscribe(name => (this.authors[id] = name))
		})
	}
}
